import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, Eraser } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDataHygieneButton — Round 105g (Jun 2026).
 *
 * Runs the non-dividend sanitizer (wipes phantom dividend fields off
 * pays_dividend=false rows — the GCT fake-19.88%-yield bug) and shows
 * the last run's dirty/cleaned counts + source (manual vs weekly cron)
 * so provider-data contamination is visible the moment it recurs.
 */
export default function AdminDataHygieneButton() {
  const { user } = useAuth();
  const [lastRun, setLastRun] = useState(null);
  const [firing, setFiring] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sanitize-non-dividend/status`, { withCredentials: true });
      setLastRun(data?.last_run || null);
    } catch { /* non-admin → silently hide */ }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    fetchStatus();
    const id = setInterval(fetchStatus, 120000);
    return () => clearInterval(id);
  }, [user, fetchStatus]);

  if (user?.role !== 'admin') return null;

  const handleFire = async () => {
    if (firing) return;
    setFiring(true);
    try {
      const { data } = await axios.post(`${API}/admin/sanitize-non-dividend`, {}, { withCredentials: true });
      toast.success(
        data.dirty === 0
          ? 'Data hygiene: all clean — no contaminated rows found'
          : `Data hygiene: cleaned ${data.cleaned} of ${data.dirty} contaminated non-dividend rows`
      );
      fetchStatus();
    } catch (err) {
      toast.error('Sanitize failed', { description: err?.response?.data?.detail || err?.message });
    } finally {
      setFiring(false);
    }
  };

  let badge = null;
  if (lastRun) {
    const when = lastRun.at ? new Date(lastRun.at).toLocaleString() : '—';
    const wasDirty = (lastRun.dirty || 0) > 0;
    badge = (
      <span
        className={`inline-flex items-center gap-1 text-[10px] ${wasDirty ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}
        title={`Last run: ${when} · source: ${lastRun.source || '?'} · ${lastRun.dirty} dirty → ${lastRun.cleaned} cleaned`}
        data-testid="admin-data-hygiene-badge"
      >
        {wasDirty ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
        {lastRun.dirty} dirty · {lastRun.cleaned} cleaned
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleFire}
        disabled={firing}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: wipe phantom dividend fields (yield/rate/frequency/dates) off non-dividend tickers. Also runs automatically after the Sunday Massive sweep. Instant, idempotent, keeps historical dividend_history."
        data-testid="admin-data-hygiene-fire"
      >
        {firing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eraser className="w-3 h-3" />}
        Data Hygiene
      </button>
      {badge}
    </div>
  );
}
